import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Fade,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
import clearLogo from '../../assets/Clear_Logo_horizontal_navy.jpg';
import polygonLogo from '../../assets/logo.png';
import { useAuthProfile } from "../../app/hooks";
import type { ClearStatus } from '../../types/onboarding';
import { ClearHaloIcon } from './ClearHaloIcon';

function getClearErrorMessage(clearStatus?: ClearStatus): React.ReactNode {
  const supportLink = <Link component={RouterLink} to="/contact">contact support</Link>;
  switch (clearStatus) {
    case 'expired':
      return 'Your verification session expired before it could be completed. Please try again.';
    case 'canceled':
      return 'Verification was canceled. Please try again when you\'re ready.';
    case 'awaiting_user_input':
      return 'Verification was not completed. Please try again.';
    case 'identity_mismatch':
      return <>The identity provided does not match our records. Please {supportLink} for assistance.</>;
    case 'clear_id_conflict':
      return <>This identity is already associated with another account. Please {supportLink} for assistance.</>;
    case 'failed':
    default:
      return <>We were unable to verify your identity with the information provided. Please try again or {supportLink} to assist with your account.</>;
  }
}

interface ReVerificationProps {
  onNext: () => void;
  phase?: 'processing' | 'success' | 'error' | null;
  clearStatus?: ClearStatus;
}

type VerificationPhase = 'intro' | 'processing' | 'success' | 'error';

// Auto-advance window for the re-verification success screen. Re-verifying users
// are one routing decision away from the records view (HealthWalletMain unmounts
// OnboardingFlow as soon as verificationStatus flips to 'verified'), so without
// this gate the success screen would flash and disappear. Initial onboarding
// doesn't need this — users have additional steps (consent, trust) that hold
// the OnboardingFlow tree mounted, so the success screen sticks naturally.
const REVERIFY_SUCCESS_AUTOADVANCE_SECONDS = 5;


export const ReVerification: FC<ReVerificationProps> = ({ onNext, phase: propsPhase, clearStatus }) => {
  const { profile: loggedInUserProfile } = useAuthProfile();
  // Phase is set by the parent via key={clearPhase} forcing a remount with propsPhase='success'|'error'.
  // Internal transitions (intro→processing) are local; parent never changes phase on a live instance.
  const [phase, setPhase] = useState<VerificationPhase>(propsPhase || 'intro');
  const [autoAdvanceCount, setAutoAdvanceCount] = useState(REVERIFY_SUCCESS_AUTOADVANCE_SECONDS);

  const isVerificationDisabled = phase === 'processing' || phase === 'success';

  // Countdown auto-advance only fires when phase is 'success'. Resets on entry,
  // calls onNext when reaching 0. Cleanup on unmount or phase change clears the
  // interval so we don't leak timers or fire onNext after the user has already
  // clicked through.
  useEffect(() => {
    if (phase !== 'success') {
      setAutoAdvanceCount(REVERIFY_SUCCESS_AUTOADVANCE_SECONDS);
      return;
    }
    const interval = setInterval(() => {
      setAutoAdvanceCount((c) => {
        if (c <= 1) {
          clearInterval(interval);
          onNext();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, onNext]);

  const handleStartVerification = async () => {
    setPhase('processing');
    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/healthwallet/onboarding/verify';
      form.style.display = 'none';
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      idInput.value = loggedInUserProfile?.id || '';
      form.appendChild(idInput);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (err) {
      console.error('Re-verification error:', err);
      setPhase('error');
    }
  };

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          pt: { xs: '88px', sm: '112px' },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }} aria-hidden="true">
              <LockIcon sx={{ color: BRAND_DARKER, fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: '#8e8e93', letterSpacing: 1.5 }}>
                Session Expired
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                Re-verify Your Identity
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth="sm" sx={{ mt: -2, pb: 6, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Logos — always visible */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Box component="img" src={clearLogo} alt="CLEAR" sx={{ height: 36, objectFit: 'contain' }} />
              <Typography sx={{ color: '#d1d5db', fontSize: '1.25rem', lineHeight: 1 }}>|</Typography>
              <Box component="img" src={polygonLogo} alt="Polygon" sx={{ height: 72, objectFit: 'contain' }} />
            </Box>
            {/* INTRO PHASE - Simplified for returning users */}
            {phase === 'intro' && (
              <Fade in>
                <Box>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                      Welcome Back!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, maxWidth: 400, mx: 'auto' }}>
                      For your security, please re-verify your identity with CLEAR to continue accessing your Health Wallet.
                    </Typography>
                  </Box>

                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { color: BRAND_DARKER } }}>
                    <Typography variant="body2">
                      <strong>Quick re-verification required.</strong> Since you've verified before, this process should be even faster. You'll be securely redirected to CLEAR.
                    </Typography>
                  </Alert>
                </Box>
              </Fade>
            )}

            {/* PROCESSING PHASE - Simple for returning users */}
            {phase === 'processing' && (
              <Fade in>
                <Box role="status" aria-live="polite" aria-busy="true" sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={48} sx={{ color: BRAND_DARKER, mb: 2 }} />
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1c1c1e' }}>
                    Re-verifying Your Identity...
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mx: 'auto' }}>
                    Please wait while we securely re-verify your identity with CLEAR. This should only take a few moments.
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* SUCCESS PHASE */}
            {phase === 'success' && (
              <Fade in>
                <Box role="status" aria-live="polite" sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: '#f0fdf4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#16a34a' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#166534', mb: 1 }}>
                    Welcome Back!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 350, mx: 'auto' }}>
                    Your identity has been successfully re-verified. You can now continue accessing your Health Wallet.
                  </Typography>
                  <Alert severity="success" sx={{ borderRadius: 2, textAlign: 'left' }}>
                    Re-verification complete. Your session has been securely restored.
                  </Alert>
                </Box>
              </Fade>
            )}

            {/* ERROR PHASE */}
            {phase === 'error' && (
              <Fade in>
                <Box role="alert" aria-live="assertive" sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>
                    Unable to verify your identity
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 350, mx: 'auto' }}>
                    {getClearErrorMessage(clearStatus)}
                  </Typography>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Action buttons - Simplified for returning users */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 3, md: 4 }, pt: 0, gap: 2 }}>
            {phase === 'intro' && (
              <Button
                variant="contained"
                onClick={handleStartVerification}
                disabled={isVerificationDisabled}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#041A55',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '16px' },
                  whiteSpace: 'nowrap',
                  py: '0.75rem',
                  px: { xs: '1rem', sm: '1.5rem' },
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#2A3D75', boxShadow: 'none' },
                  '&:active': { bgcolor: '#1A294D', transform: 'scale(0.98)', boxShadow: 'none' },
                  '&:disabled': { opacity: 0.6, bgcolor: '#041A55', color: '#fff' },
                }}
              >
                <ClearHaloIcon />
                Verify with CLEAR
              </Button>
            )}

            {phase === 'success' && (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                sx={{
                  bgcolor: BRAND_DARKER,
                  borderRadius: 50,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  '&:hover': { bgcolor: '#5a3fc0' },
                }}
              >
                Continue to Health Wallet ({autoAdvanceCount})
              </Button>
            )}

            {phase === 'error' && (
              <Button
                variant="contained"
                onClick={handleStartVerification}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#041A55',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '13px', sm: '16px' },
                  whiteSpace: 'nowrap',
                  py: '0.75rem',
                  px: { xs: '1rem', sm: '1.5rem' },
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#2A3D75', boxShadow: 'none' },
                  '&:active': { bgcolor: '#1A294D', transform: 'scale(0.98)', boxShadow: 'none' },
                }}
              >
                <ClearHaloIcon />
                Verify with CLEAR
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
